/**
 * Age verification hook.
 *
 * Manages the verification lifecycle: start, poll status, opt-in.
 * Returns UI-friendly state and the age_gate per-method breakdown.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { csrfHeaders } from '@adieuu/shared';
import { useAppConfig } from '../config';
import { useAuth } from './useAuth';
import { usePlatform } from './usePlatform';
import { appIdFromVerificationUrl, openVerificationUrl } from '../services/openVerificationUrl';

export type AgeVerificationUIStatus =
  | 'idle'
  | 'starting'
  | 'checking_email'
  | 'email_check_inconclusive'
  /** Email reached no verdict and the jurisdiction allows no other method. */
  | 'email_only_inconclusive'
  | 'awaiting_user'
  | 'polling'
  | 'approved'
  | 'failed'
  | 'expired'
  | 'subscription_required';

interface MethodAttemptInfo {
  enabled: boolean;
  maxAttempts: number;
  remaining: number;
}

export interface UseAgeVerificationReturn {
  status: AgeVerificationUIStatus;
  verificationId?: string;
  ageGate?: Record<string, MethodAttemptInfo>;
  /** Seconds until the next status poll, or null when not polling. */
  secondsUntilNextPoll: number | null;
  /** Provider reference id for the attempt in progress. */
  referenceId?: string;
  /** Agemin asset id parsed from the hosted URL, when that URL has one. */
  appId?: string;
  /** Present when status is 'subscription_required'; distinguishes SUBSCRIPTION_REQUIRED from SUBSCRIPTION_EXPIRED. */
  billingCode?: string;
  start: () => Promise<void>;
  optIn: (country?: string) => Promise<void>;
  /** Opens the stored provider interactive flow URL after an inconclusive email check. */
  continueInteractive: () => Promise<void>;
  /** Polls the provider immediately and restarts the backoff at the base interval. */
  checkNow: () => Promise<void>;
  /** Reopens the hosted verification window for this attempt. */
  reopen: () => Promise<void>;
  /** True once a hosted URL is stored, so the in-progress screen can offer reopen. */
  canReopen: boolean;
  cancel: () => void;
}

const POLL_INTERVAL_MS = 15000;
const MAX_POLL_INTERVAL_MS = 30000;
/** Short interval used while waiting for a background check to resolve. */
const BG_CHECK_POLL_INTERVAL_MS = 3000;
/** Max attempts before declaring background check inconclusive. */
const BG_CHECK_MAX_POLLS = 4;

export function useAgeVerification(): UseAgeVerificationReturn {
  const { apiBaseUrl } = useAppConfig();
  const { refreshSession } = useAuth();
  const platform = usePlatform();

  const [status, setStatus] = useState<AgeVerificationUIStatus>('idle');
  const [verificationId, setVerificationId] = useState<string>();
  const [ageGate, setAgeGate] = useState<Record<string, MethodAttemptInfo>>();
  const [billingCode, setBillingCode] = useState<string>();
  const [secondsUntilNextPoll, setSecondsUntilNextPoll] = useState<number | null>(null);
  const [referenceId, setReferenceId] = useState<string>();
  const [appId, setAppId] = useState<string>();
  const [canReopen, setCanReopen] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelledRef = useRef(false);
  const providerVerificationIdRef = useRef<string>();
  const hostedUrlRef = useRef<string>();
  const pollTokenRef = useRef(0);
  const openGenerationRef = useRef(0);
  const closeWatchStopRef = useRef<(() => void) | null>(null);
  const checkNowRef = useRef<() => Promise<void>>(async () => {});

  const stopCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setSecondsUntilNextPoll(null);
  }, []);

  const startCountdown = useCallback((intervalMs: number) => {
    stopCountdown();
    setSecondsUntilNextPoll(Math.ceil(intervalMs / 1000));
    countdownRef.current = setInterval(() => {
      setSecondsUntilNextPoll((prev) => {
        if (prev === null || prev <= 1) return null;
        return prev - 1;
      });
    }, 1000);
  }, [stopCountdown]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
    stopCountdown();
  }, [stopCountdown]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    stopPolling();
    setStatus('idle');
    setVerificationId(undefined);
    setAgeGate(undefined);
    setBillingCode(undefined);
    setReferenceId(undefined);
    setAppId(undefined);
    setCanReopen(false);
    hostedUrlRef.current = undefined;
    openGenerationRef.current += 1;
    closeWatchStopRef.current?.();
    closeWatchStopRef.current = null;
  }, [stopPolling]);

  const pollStatus = useCallback(
    async (pvId: string, interval: number, scheduleAfterMs?: number) => {
      const token = ++pollTokenRef.current;
      if (cancelledRef.current) return;

      const scheduleNext = (nextInterval: number) => {
        if (cancelledRef.current || token !== pollTokenRef.current) return;
        startCountdown(nextInterval);
        pollingRef.current = setTimeout(() => pollStatus(pvId, nextInterval), nextInterval);
      };

      try {
        const response = await fetch(`${apiBaseUrl}/api/age-verification/status?id=${encodeURIComponent(pvId)}`, {
          credentials: 'include',
        });

        if (token !== pollTokenRef.current || cancelledRef.current) return;
        if (!response.ok) return;

        const json = (await response.json()) as {
          data?: {
            status: string;
            methodAttempts?: Record<string, MethodAttemptInfo>;
          };
        };

        const data = json.data;
        if (!data) return;

        if (data.methodAttempts) {
          setAgeGate(data.methodAttempts);
        }

        if (data.status === 'approved') {
          setStatus('approved');
          stopPolling();
          await refreshSession();
          return;
        }
        if (data.status === 'failed') {
          setStatus('failed');
          stopPolling();
          await refreshSession();
          return;
        }
        if (data.status === 'expired') {
          setStatus('expired');
          stopPolling();
          await refreshSession();
          return;
        }

        if (data.status === 'pending') {
          setStatus('polling');
        }

        scheduleNext(scheduleAfterMs ?? Math.min(interval * 1.5, MAX_POLL_INTERVAL_MS));
      } catch {
        scheduleNext(scheduleAfterMs ?? Math.min(interval * 2, MAX_POLL_INTERVAL_MS));
      }
    },
    [apiBaseUrl, refreshSession, startCountdown, stopPolling],
  );

  const checkNow = useCallback(async () => {
    const pvId = providerVerificationIdRef.current;
    if (!pvId || cancelledRef.current) return;
    stopPolling();
    await pollStatus(pvId, POLL_INTERVAL_MS, POLL_INTERVAL_MS);
  }, [pollStatus, stopPolling]);

  checkNowRef.current = checkNow;

  const openHosted = useCallback(async (url: string) => {
    hostedUrlRef.current = url;
    setAppId(appIdFromVerificationUrl(url));
    setCanReopen(true);
    closeWatchStopRef.current?.();
    const generation = ++openGenerationRef.current;
    const handle = await openVerificationUrl(url, platform);
    closeWatchStopRef.current = handle.stop;
    void handle.closed.then(() => {
      if (generation !== openGenerationRef.current || cancelledRef.current) return;
      return checkNowRef.current();
    });
  }, [platform]);

  const pollBackgroundCheck = useCallback(
    async (pvId: string, attempt: number) => {
      if (cancelledRef.current) return;

      try {
        const response = await fetch(`${apiBaseUrl}/api/age-verification/status?id=${encodeURIComponent(pvId)}`, {
          credentials: 'include',
        });

        if (response.ok) {
          const json = (await response.json()) as {
            data?: { status: string; methodAttempts?: Record<string, MethodAttemptInfo> };
          };
          const data = json.data;

          if (data?.status === 'approved') {
            setStatus('approved');
            stopPolling();
            await refreshSession();
            return;
          }
          if (data?.status === 'failed') {
            setStatus('failed');
            stopPolling();
            await refreshSession();
            return;
          }
          if (data?.methodAttempts) {
            setAgeGate(data.methodAttempts);
          }
        }
      } catch {
        // Network error — treat as inconclusive after retries
      }

      if (attempt + 1 >= BG_CHECK_MAX_POLLS) {
        stopPolling();
        setStatus('email_check_inconclusive');
        return;
      }

      if (!cancelledRef.current) {
        startCountdown(BG_CHECK_POLL_INTERVAL_MS);
        pollingRef.current = setTimeout(
          () => pollBackgroundCheck(pvId, attempt + 1),
          BG_CHECK_POLL_INTERVAL_MS,
        );
      }
    },
    [apiBaseUrl, refreshSession, startCountdown, stopPolling],
  );

  const startFlow = useCallback(
    async (endpoint: string, body?: Record<string, unknown>) => {
      cancelledRef.current = false;
      setStatus('starting');

      try {
        const response = await fetch(`${apiBaseUrl}/api/${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
          credentials: 'include',
          body: body ? JSON.stringify(body) : undefined,
        });

        if (!response.ok) {
          try {
            const errJson = (await response.json()) as { code?: string };
            if (errJson?.code === 'SUBSCRIPTION_REQUIRED' || errJson?.code === 'SUBSCRIPTION_EXPIRED') {
              setBillingCode(errJson.code);
              setStatus('subscription_required');
              return;
            }
          } catch { /* fall through to generic failure */ }
          setStatus('failed');
          return;
        }

        const json = (await response.json()) as {
          data?: {
            verificationId: string;
            providerVerificationId: string;
            status: string;
            redirectUrl?: string;
            backgroundCheckAttempted?: boolean;
          };
        };

        const data = json.data;
        if (!data) {
          setStatus('failed');
          return;
        }

        setVerificationId(data.verificationId);
        providerVerificationIdRef.current = data.providerVerificationId;
        setReferenceId(data.providerVerificationId);
        if (data.redirectUrl) {
          hostedUrlRef.current = data.redirectUrl;
          setAppId(appIdFromVerificationUrl(data.redirectUrl));
          setCanReopen(true);
        }

        if (data.status === 'approved') {
          setStatus('approved');
          await refreshSession();
          return;
        }

        // Nothing is in flight and there is no other method to offer, so stop
        // here rather than polling a verification that was never started.
        if (data.status === 'inconclusive') {
          setStatus('email_only_inconclusive');
          return;
        }

        if (data.status === 'failed') {
          setStatus('failed');
          await refreshSession();
          return;
        }

        if (data.backgroundCheckAttempted && data.redirectUrl) {
          setStatus('checking_email');
          startCountdown(BG_CHECK_POLL_INTERVAL_MS);
          pollingRef.current = setTimeout(
            () => pollBackgroundCheck(data.providerVerificationId, 0),
            BG_CHECK_POLL_INTERVAL_MS,
          );
        } else if (data.redirectUrl) {
          setStatus('awaiting_user');
          await openHosted(data.redirectUrl);
          startCountdown(POLL_INTERVAL_MS);
          pollingRef.current = setTimeout(
            () => pollStatus(data.providerVerificationId, POLL_INTERVAL_MS),
            POLL_INTERVAL_MS,
          );
        } else {
          setStatus('polling');
          startCountdown(POLL_INTERVAL_MS);
          pollingRef.current = setTimeout(
            () => pollStatus(data.providerVerificationId, POLL_INTERVAL_MS),
            POLL_INTERVAL_MS,
          );
        }
      } catch {
        setStatus('failed');
      }
    },
    [apiBaseUrl, openHosted, pollBackgroundCheck, pollStatus, refreshSession, startCountdown],
  );

  const start = useCallback(() => startFlow('age-verification/start'), [startFlow]);

  const optIn = useCallback(
    (country?: string) => startFlow('age-verification/opt-in', { country }),
    [startFlow],
  );

  const continueInteractive = useCallback(async () => {
    const url = hostedUrlRef.current;
    const pvId = providerVerificationIdRef.current;
    if (!url || !pvId) return;

    setStatus('awaiting_user');
    await openHosted(url);
    startCountdown(POLL_INTERVAL_MS);
    pollingRef.current = setTimeout(
      () => pollStatus(pvId, POLL_INTERVAL_MS),
      POLL_INTERVAL_MS,
    );
  }, [openHosted, pollStatus, startCountdown]);

  const reopen = useCallback(async () => {
    const url = hostedUrlRef.current;
    if (!url) return;
    await openHosted(url);
  }, [openHosted]);

  // Listen for postMessage from callback page (origin-checked)
  useEffect(() => {
    const expectedOrigin = apiBaseUrl || window.location.origin;
    const handler = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin) return;
      if (event.data?.type === 'age-verification-callback') {
        const callbackStatus = event.data.status;
        if (callbackStatus === 'approved' || callbackStatus === 'failed' || callbackStatus === 'expired') {
          setStatus(callbackStatus as AgeVerificationUIStatus);
          stopPolling();
          refreshSession();
        }
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [apiBaseUrl, refreshSession, stopPolling]);

  // Cleanup on unmount
  useEffect(() => () => {
    openGenerationRef.current += 1;
    closeWatchStopRef.current?.();
    stopPolling();
  }, [stopPolling]);

  return {
    status,
    verificationId,
    ageGate,
    billingCode,
    secondsUntilNextPoll,
    referenceId,
    appId,
    canReopen,
    start,
    optIn,
    continueInteractive,
    checkNow,
    reopen,
    cancel,
  };
}
